import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, Loader2 } from "lucide-react";

export default function SettingsAdmin() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", s);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!s) return <div className="grid place-items-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin"/></div>;

  const setField = (key, value) => setS({ ...s, [key]: value });
  const updateItem = (key, i, patch) => {
    const list = [...(s[key] || [])]; list[i] = { ...list[i], ...patch }; setField(key, list);
  };
  const addItem = (key, item) => setField(key, [...(s[key] || []), item]);
  const removeItem = (key, i) => setField(key, (s[key] || []).filter((_, ix) => ix !== i));

  return (
    <div data-testid="admin-settings">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tighter">Site settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Global configuration for the public site (homepage stats, brand strip, buying-guide links, footer).</p>
        </div>
        <button data-testid="settings-save" onClick={save} disabled={busy} className="btn-primary h-10 px-4 text-sm inline-flex items-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Save
        </button>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        {/* Trust stats */}
        <div className="rounded-3xl border border-[#E5E7EB] bg-white p-5" data-testid="settings-trust">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Trust stats (hero bottom strip)</div>
          {(s.trust_stats || []).map((t, i) => (
            <div key={i} className="flex gap-2 mb-2" data-testid={`trust-row-${i}`}>
              <input value={t.label} onChange={(e) => updateItem("trust_stats", i, { label: e.target.value })} placeholder="Label" className={inp}/>
              <input value={t.value} onChange={(e) => updateItem("trust_stats", i, { value: e.target.value })} placeholder="Value" className={inp}/>
              <button onClick={() => removeItem("trust_stats", i)} data-testid={`trust-del-${i}`} className="h-10 w-10 rounded-lg border border-[#E5E7EB] text-rose-600 grid place-items-center"><Trash2 className="w-3 h-3"/></button>
            </div>
          ))}
          <button data-testid="trust-add" onClick={() => addItem("trust_stats", { label: "", value: "" })}
            className="mt-2 h-9 px-3 rounded-full border border-[#E5E7EB] bg-white text-sm inline-flex items-center gap-1 hover:bg-[#F3F6FA]">
            <Plus className="w-3 h-3"/> Add stat
          </button>
        </div>

        {/* Brand strip */}
        <div className="rounded-3xl border border-[#E5E7EB] bg-white p-5" data-testid="settings-brand-strip">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Brand strip source</div>
          <select value={s.brand_strip_mode} onChange={(e) => setField("brand_strip_mode", e.target.value)}
            data-testid="brand-strip-mode" className={inp}>
            <option value="featured">Use all brands where featured=true</option>
            <option value="manual">Manual list of brand slugs (below)</option>
          </select>
          {s.brand_strip_mode === "manual" && (
            <textarea data-testid="brand-strip-slugs"
              value={(s.brand_strip_slugs || []).join(", ")}
              onChange={(e) => setField("brand_strip_slugs", e.target.value.split(",").map((v) => v.trim()).filter(Boolean))}
              placeholder="tata, maruti-suzuki, hyundai, ..."
              className={`${inp} mt-3 min-h-[80px] py-2`}/>
          )}
          <div className="text-[11px] text-muted-foreground mt-2">Toggle a brand&apos;s Featured flag in the Brands module to include it.</div>
        </div>

        {/* Best of links */}
        <div className="rounded-3xl border border-[#E5E7EB] bg-white p-5 lg:col-span-2" data-testid="settings-best-of">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Buying guide / Best-of links (homepage)</div>
          {(s.best_of_links || []).map((b, i) => (
            <div key={i} className="flex gap-2 mb-2" data-testid={`bestof-row-${i}`}>
              <input value={b.slug} onChange={(e) => updateItem("best_of_links", i, { slug: e.target.value })} placeholder="slug (e.g. suvs)" className={inp}/>
              <input value={b.label} onChange={(e) => updateItem("best_of_links", i, { label: e.target.value })} placeholder="Label" className={inp}/>
              <button onClick={() => removeItem("best_of_links", i)} data-testid={`bestof-del-${i}`} className="h-10 w-10 rounded-lg border border-[#E5E7EB] text-rose-600 grid place-items-center"><Trash2 className="w-3 h-3"/></button>
            </div>
          ))}
          <button data-testid="bestof-add" onClick={() => addItem("best_of_links", { slug: "", label: "" })}
            className="mt-2 h-9 px-3 rounded-full border border-[#E5E7EB] bg-white text-sm inline-flex items-center gap-1 hover:bg-[#F3F6FA]">
            <Plus className="w-3 h-3"/> Add link
          </button>
        </div>

        {/* Footer tagline */}
        <div className="rounded-3xl border border-[#E5E7EB] bg-white p-5 lg:col-span-2" data-testid="settings-footer">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Footer tagline</div>
          <input data-testid="footer-tagline" value={s.footer_tagline || ""} onChange={(e) => setField("footer_tagline", e.target.value)} className={inp}/>
        </div>
      </div>
    </div>
  );
}

const inp = "flex-1 h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm outline-none focus:border-[#2563EB]";
